<script setup lang="ts">
import { ref, watch, getCurrentInstance } from "vue";

import type { TabItem } from "@/types/ui";

import { appStateStore } from "@/stores/appState";

import { useComponentErrorCapture } from "@/mixins/error";

const instanceUid = getCurrentInstance()?.uid || Math.floor(Math.random() * 1000);
const props = defineProps({
  listData: {
    type: Array<TabItem>,
    default: [],
  },
  activeTab: {
    type: Object,
    default: {},
  },
  compact: {
    type: Boolean,
    default: false,
  },
  responsive: {
    type: Boolean,
    default: true,
  },
});

const appState = appStateStore();
useComponentErrorCapture();

const emit = defineEmits(["itemClick"]);
const selectValue = ref(props.activeTab);
const selectOpened = ref(false);

watch(props, () => {
  if (props.activeTab != selectValue.value) {
    selectValue.value = props.activeTab;
  }
});

watch(selectOpened, (val) => {
  appState.activeDropdown = val;
});

function itemClick(item: TabItem) {
  emit("itemClick", item);
  if (item) {
    selectValue.value = item;
  }
}

function onSelectUpdate(item: any) {
  emit("itemClick", item);
  selectValue.value = item;
}

</script>
<template>
  <div class="tab-menu" :class="{'tab-menu--compact': compact, 'tab-menu--no-responsive': !responsive}">
    <ul class="tab-menu__list">
      <li
        v-for="item in listData"
        @click="itemClick(item)"
        :class="{
          'active': activeTab.value == item.value,
          'disabled': item.disabled === true
        }"
        class="tab-menu__list__item">
        <i v-if="item.icon" class="tab-menu__list__item__icon" :class="item.icon"></i>
        <span v-if="item.label" class="tab-menu__list__item__label">{{ item.label }}</span>
      </li>
    </ul>
    <Transition name="opacity">
      <div v-show="selectOpened" class="tab-menu__select-background"></div>
    </Transition>
    <v-select
      v-model="selectValue"
      @update:modelValue="onSelectUpdate"
      @open="selectOpened = true"
      @close="selectOpened = false"
      :options="listData"
      :searchable="false"
      :filterable="false"
      :clearable="false"
      :selectable="(option: TabItem) => !option.disabled"
      data-nofocus
      class="v-select--on-center-list tab-menu__select"
      :id="`tab-menu__select-${instanceUid}`"
    >
			<template v-slot:option="option">
        <i v-if="option.icon" :class="`${ option.icon }`"></i>
        <span class="text">{{ option.label }}</span>
			</template>
    </v-select>
  </div>
</template>